"use client";

import { Flex, Text, type TextProps } from "@chakra-ui/react";
import { useTenantCurrency } from "@/hooks";
import { splitCurrencyAmountForDisplay } from "@/lib/utils/currency";

/** Relative to the main amount so cents stay smaller at every size. */
export const USD_CENTS_FONT_SIZE = "0.75em";
export const USD_CENTS_FONT_SIZE_INLINE = "0.75em";

interface UsdAmountTextProps {
  amount: number;
  currency?: string;
  textProps?: TextProps;
  centsProps?: TextProps;
  centsFontSize?: string;
}

/** Currency amount with emphasized cents suffix - summary cards and inline copy. */
export function UsdAmountText({
  amount,
  currency: currencyOverride,
  textProps,
  centsProps,
  centsFontSize = USD_CENTS_FONT_SIZE,
}: UsdAmountTextProps) {
  const tenantCurrency = useTenantCurrency();
  const currency = currencyOverride ?? tenantCurrency;
  const { main, cents } = splitCurrencyAmountForDisplay(amount, currency);

  return (
    <Flex as="span" align="baseline" display="inline-flex">
      <Text as="span" {...textProps}>
        {main}
        {cents ? (
          <Text
            as="span"
            display="inline"
            fontSize={centsFontSize}
            fontWeight={textProps?.fontWeight}
            color={textProps?.color}
            {...centsProps}
          >
            {cents}
          </Text>
        ) : null}
      </Text>
    </Flex>
  );
}

interface InlineUsdAmountWithCodeProps {
  amount: number;
  currency?: string;
  amountProps?: TextProps;
  centsProps?: TextProps;
}

/** Inline sentence copy, e.g. Today's signal - `$30,565.00 USD`. */
export function InlineUsdAmountWithCode({
  amount,
  currency: currencyOverride,
  amountProps,
  centsProps,
}: InlineUsdAmountWithCodeProps) {
  const tenantCurrency = useTenantCurrency();
  const currency = currencyOverride ?? tenantCurrency;

  return (
    <>
      <UsdAmountText
        amount={amount}
        currency={currency}
        textProps={amountProps}
        centsProps={centsProps}
        centsFontSize={USD_CENTS_FONT_SIZE_INLINE}
      />
      {` ${currency}`}
    </>
  );
}
